import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '../client';
import type { ConnectedAppsResponse } from '../types';

export const CONNECTED_APPS_KEY = ['connectedApps'] as const;

/**
 * Lists the OAuth apps the current user authorized.
 */
export function useConnectedApps(): UseQueryResult<ConnectedAppsResponse, Error> {
    return useQuery({
        queryKey: CONNECTED_APPS_KEY,
        queryFn: () => api.get<ConnectedAppsResponse>('/api/account/connected-apps'),
    });
}

/**
 * Revokes one authorized app and drops it from the cached list.
 */
export function useRevokeConnectedApp(): UseMutationResult<void, Error, number> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete<void>(`/api/account/connected-apps/${id}`),
        onSuccess: (_, revokedId) => {
            queryClient.setQueryData<ConnectedAppsResponse>(CONNECTED_APPS_KEY, (old) => {
                if (!old) return old;
                return {
                    ...old,
                    items: old.items.filter((app) => app.id !== revokedId),
                };
            });
        },
    });
}
